import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'antd';
import { CheckOutlined, CopyOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';

const COPIED_FOR_MS = 2000;

/**
 * Copies a table's full link; says "Copied" for 2 seconds. `ghost` is the quiet phone variant. A
 * clipboard the browser refuses says so in a toast and is reported to Sentry.
 */
export default function CopyLinkButton({ link, name, ghost = false, disabled = false }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Takeout:Tables:TableList' });
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
    } catch (error) {
      Sentry.captureException(error, { level: 'warning', tags: { feature: 'table_ordering', action: 'copy_link' } });
      toast.error(t('toasts.copy_failed'));

      return;
    }

    setCopied(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), COPIED_FOR_MS);
  };

  return (
    <Button
      size="small"
      type={ghost ? 'text' : 'default'}
      icon={copied ? <CheckOutlined /> : <CopyOutlined />}
      onClick={copy}
      disabled={disabled || !link}
      aria-label={copied ? t('aria.copied') : t('aria.copy', { name })}
      data-testid="table-copy-link"
    >
      <span aria-live="polite">{copied ? t('buttons.copied') : t('buttons.copy')}</span>
    </Button>
  );
}
